import { Building2Icon, DownloadIcon, LogOutIcon, MoveHorizontalIcon, SettingsIcon } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { useMe, useSession, useSwitchWorkspace } from "@/api/hooks/auth";
import { UserAvatar } from "@/components/common/user-avatar";
import { isThemePreference, THEME_OPTIONS, themeOption } from "@/components/theme/theme-options";
import { useAccountTheme } from "@/components/theme/use-account-theme";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { switchOrganisation } from "@/lib/sso";
import { useContentWidth } from "@/lib/use-content-width";
import { workspaceChoices, workspaceValue } from "@/lib/workspaces";

import { APP_HOME, ROLE_LABELS, SETTINGS_NAV } from "./nav-config";
import { useInstallApp } from "./use-install-app";
import { useSignOut } from "./use-sign-out";

/** Avatar button with the account menu: settings, theme, full width and sign out. */
export function UserMenu() {
  const me = useMe();
  const { theme, setTheme } = useAccountTheme();
  const [width, setWidth] = useContentWidth();
  const signOut = useSignOut();
  const installApp = useInstallApp();
  const session = useSession().data;
  const switchWorkspace = useSwitchWorkspace();
  const navigate = useNavigate();
  if (!me) return null;
  const workspaces = workspaceChoices(session);
  const currentTheme = themeOption(theme);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="rounded-full" aria-label="Account menu">
          <UserAvatar name={me.name} tone="brand" className="size-7" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-3 py-2 font-normal">
          <UserAvatar name={me.name} tone="brand" className="size-9" />
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-medium text-foreground">{me.name}</p>
              <Badge variant="brand" className="shrink-0">
                {ROLE_LABELS[me.role]}
              </Badge>
            </div>
            <p className="truncate text-xs text-muted-foreground">{me.email}</p>
            {/* The organization: the one a tenant admin works in on the root site, else their institution. */}
            {(session?.workspace?.name ?? me.institution) && (
              <p className="truncate text-xs text-muted-foreground">{session?.workspace?.name ?? me.institution}</p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.length > 0 && (
          <>
            {/* On the root site: which of their organisations the person works in (contract §13, row 4). An
                organisation signs in again for it; the root site's own courses switch here. */}
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Switch organisation
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={workspaceValue(session?.workspace?.id)}
              onValueChange={(value) => {
                const picked = workspaces.find((workspace) => workspaceValue(workspace.id) === value);
                if (picked?.hume_org) switchOrganisation(picked.hume_org, APP_HOME);
                else if (picked) switchWorkspace.mutate(undefined, { onSuccess: () => navigate(APP_HOME) });
              }}
            >
              {workspaces.map((workspace) => (
                <DropdownMenuRadioItem key={workspaceValue(workspace.id)} value={workspaceValue(workspace.id)}>
                  <Building2Icon />
                  {workspace.name}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem asChild>
          <Link to={SETTINGS_NAV.to}>
            <SettingsIcon />
            {SETTINGS_NAV.label}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <currentTheme.icon />
            Theme
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={theme}
              onValueChange={(value) => isThemePreference(value) && setTheme(value)}
            >
              {THEME_OPTIONS.map((option) => (
                <DropdownMenuRadioItem key={option.value} value={option.value}>
                  <option.icon />
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {/* Lifts the page width cap in every Hume app (lib/content-width). */}
        <DropdownMenuCheckboxItem
          checked={width === "full"}
          onCheckedChange={(checked) => setWidth(checked ? "full" : "standard")}
        >
          <MoveHorizontalIcon />
          Full width
        </DropdownMenuCheckboxItem>
        {installApp && (
          <DropdownMenuItem onSelect={installApp}>
            <DownloadIcon />
            Install app
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={signOut}>
          <LogOutIcon />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
